import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2, Cpu } from "lucide-react";
import axios from "axios";
import { CHAT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  try {
    let s = localStorage.getItem("spoorti_chat_session");
    if (!s) {
      s = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("spoorti_chat_session", s);
    }
    return s;
  } catch {
    return `web_${Date.now()}`;
  }
}

const SUGGESTED = [
  "What does Spoorti do?",
  "How can AI help my factory?",
  "Tell me about IoT services",
  "Book a demo",
];

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm the Spoorti Assistant. Ask me about our AI, IoT, Edge or Cloud services — or how we can help your business.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I couldn't reach our AI right now. Please email info@spoortitechnologies.in and we'll respond quickly.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid={CHAT.toggle}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close assistant" : "Open assistant"}
        className="fixed bottom-24 right-6 z-[10000] w-14 h-14 rounded-full grid place-items-center text-white shadow-2xl shadow-cyan-500/30 btn-primary"
      >
        <span className="absolute inset-0 rounded-full bg-cyan-500/30 opacity-70 animate-ping" />
        <Bot className="relative w-7 h-7" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT.window}
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-44 right-6 z-[10000] w-[min(92vw,400px)] h-[min(70vh,560px)] flex flex-col rounded-2xl border border-white/10 bg-[#0F172A] shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-blue-700/40 to-cyan-600/30">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 grid place-items-center">
                  <Cpu className="w-4 h-4 text-white" />
                </span>
                <div>
                  <div className="font-heading font-semibold leading-none">Spoorti Assistant</div>
                  <div className="text-[11px] text-emerald-300 flex items-center gap-1 mt-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Online · Powered by AI
                  </div>
                </div>
              </div>
              <button
                data-testid={CHAT.close}
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/10"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div
              ref={scrollerRef}
              data-testid={CHAT.messages}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-blue-600 to-cyan-500 text-white rounded-br-sm"
                        : "bg-[#131C2F] text-slate-100 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#131C2F] border border-white/10 rounded-2xl px-3.5 py-2.5 text-sm text-slate-300 inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
                    Thinking…
                  </div>
                </div>
              )}
              {messages.length <= 1 && !loading && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 p-3 bg-[#0B0F19]">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid={CHAT.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Ask about AI, IoT, Edge…"
                  className="flex-1 resize-none bg-[#131C2F] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/60 max-h-28"
                />
                <button
                  data-testid={CHAT.send}
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="btn-primary w-10 h-10 rounded-xl grid place-items-center text-white disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
